import Drawable from '../entities/Drawable.js';
import Sprite from './Sprite.js';
import PixelText from './PixelText.js';
import StatusBadge from './StatusBadge.js';
import { imageStore } from '../images.js';
import { c } from '../setup.js';

export default class SecondaryPokemonBox extends Drawable {
    constructor({ pokemonMenu, pokemon, position = { x: 0, y: 0 }, visible = true, order }) {
        super();
        this.order = order;
        this.pokemonMenu = pokemonMenu;
        this.pokemon = pokemon;
        this.sprites = {};
        this.position = position;
        this.visible = visible;

        // Perhaps because we create multiple instances of this
        // one, but using the sprite from the sprite store results
        // in loading only the last secondary box. We gotta think about
        // how to handle this scenario.
        this.sprites.background = new Sprite({
            image: imageStore.get('img/pokemon_menu_secondary_box.png'),
            moving: false,
            position: { x: 0, y: 0 },
            visible: false,
            frames: { val: 0, max: 2 },
        });
        this.sprites.background.position = position;
        this.sprites.pokemonIcon = this.pokemon?.icon;
        if (this.sprites.pokemonIcon && this.pokemon) {
            this.sprites.pokemonIcon.spriteIndex = this.pokemon.id - 1;
        }
        this.canLearnMoveText = new PixelText({
            text: 'UNABLE',
            position: { x: this.position.x + 65, y: this.position.y + 37 - 17 - 7 },
            color: 'white',
        });
        this.pokemonNameText = new PixelText({
            text: this.pokemon?.nickname || this.pokemon?.name,
            position: { x: this.position.x + 30, y: this.position.y + 29 - 17 - 7 },
            color: 'white',
        });
        this.pokemonLevelText = new PixelText({
            text: `Lv${this.pokemon?.level}`,
            position: { x: this.position.x + 38, y: this.position.y + 37 - 17 - 7 },
            color: 'white',
        });

        let genderString = '';
        let genderColor = 'black';
        if (this.pokemon?.gender === 'Female') {
            genderString = '  \u2640 ';
            genderColor = 'red';
        }
        if (this.pokemon?.gender === 'Male') {
            genderString = '  \u2642 ';
            genderColor = 'blue';
        }

        this.pokemonGenderText = new PixelText({
            text: genderString,
            position: {
                x: this.pokemonLevelText.position.x + 16,
                y: this.pokemonLevelText.position.y - 7,
            },
            color: genderColor,
        });

        this.pokemonHpText = new PixelText({
            text: `${this.pokemon?.stats.hp}/${this.pokemon?.stats.maxHp}`,
            textAlign: 'right',
            position: { x: this.position.x + 144, y: this.position.y + 21 - 7 },
            color: 'white',
        });
        this.animationCursor = 0;
        this.sprites.statusBadge = new StatusBadge({
            position: { x: this.position.x + 30, y: this.position.y + 13 },
            visible: true,
            status: this.pokemon?.status1 ? this.pokemon.status1.id : 'blank',
        });
        this.sprites.heldItemIcon = new Sprite({
            position: { x: this.position.x + 15, y: this.position.y + 13 },
            image: imageStore.get('img/held_item.png'),
            visible: false,
            moving: false,
        });
    }

    draw() {
        if (!this.pokemon) {
            return;
        }
        this.sprites.pokemonIcon = this.pokemon.icon;
        this.sprites.pokemonIcon.spriteIndex = this.pokemon.id - 1;
        this.visible = this.pokemonMenu.visible;
        this.sprites.background.visible = this.pokemonMenu.visible;

        if (!this.visible) {
            return;
        }

        const currentlySelected = this.pokemonMenu.currentOption === this.order;
        this.animationCursor += 1;
        if (this.animationCursor > 30) {
            this.animationCursor = 0;
        }

        if (this.animationCursor < 15) {
            this.sprites.pokemonIcon.position.y = this.position.y + 8 - 14;
        } else {
            this.sprites.pokemonIcon.position.y = this.position.y + (currentlySelected ? 4 : 7) - 14;
        }
        this.sprites.pokemonIcon.position.x = this.position.x - 6;

        this.sprites.background.frames.val = currentlySelected ? 0 : 1;
        this.sprites.background.draw();
        this.pokemonNameText.text = this.pokemon?.nickname || this.pokemon?.name;
        this.pokemonNameText.draw();
        if (!this.pokemon.status1) {
            this.pokemonLevelText.text = `Lv${this.pokemon?.level}`;
            let genderString = '';
            let genderColor = 'black';
            if (this.pokemon?.gender === 'Female') {
                genderString = '  \u2640 ';
                genderColor = 'red';
            }
            if (this.pokemon?.gender === 'Male') {
                genderString = '  \u2642 ';
                genderColor = 'blue';
            }
            this.pokemonGenderText.text = genderString;
            this.pokemonGenderText.color = genderColor;
            this.pokemonGenderText.position.x = this.pokemonLevelText.position.x + 16;
            this.pokemonGenderText.position.y = this.pokemonLevelText.position.y;
            this.pokemonGenderText.draw();
            this.pokemonLevelText.draw();
        }
        if (this.pokemonMenu.shouldShowMoveLearnability) {
            const moveToBeLearned = this.pokemonMenu.GUI.stateMachine.context.currentMove;
            this.canLearnMoveText.text = this.pokemon.moveLearnabilityText(moveToBeLearned);
            this.canLearnMoveText.draw();
        } else {
            this.canLearnMoveText.text = '';
        }
        this.pokemonHpText.text = `${this.pokemon.stats.hp}/${this.pokemon.stats.maxHp}`;
        this.pokemonHpText.draw();
        this.sprites.pokemonIcon.visible = true;
        this.sprites.pokemonIcon.draw();
        const barShouldBeRed = this.pokemon.stats.hp < this.pokemon.stats.maxHp * 0.2;
        const barShouldBeYellow = this.pokemon.stats.hp < this.pokemon.stats.maxHp * 0.5;

        c.fillStyle = Drawable.blackBarColor;
        c.fillRect(this.position.x + 96, this.position.y + 9, 48, 3);

        // Health Bar
        c.fillStyle = barShouldBeRed
            ? Drawable.redBarColor
            : barShouldBeYellow
            ? Drawable.yellowBarColor
            : Drawable.greenBarColor;
        c.fillRect(
            this.position.x + 96,
            this.position.y + 9,
            Math.ceil(48 * (this.pokemon.stats.hp / this.pokemon.stats.maxHp)),
            3
        );
        c.fillStyle = barShouldBeRed
            ? Drawable.redBarShadow
            : barShouldBeYellow
            ? Drawable.yellowBarShadow
            : Drawable.greenBarShadow;
        c.fillRect(
            this.position.x + 96,
            this.position.y + 9,
            Math.ceil(48 * (this.pokemon.stats.hp / this.pokemon.stats.maxHp)),
            1
        );
        if (this.pokemon.status1) {
            this.sprites.statusBadge.visible = true;
            this.sprites.statusBadge.status = this.pokemon.status1.id;
            this.sprites.statusBadge.draw();
        }
        if (this.pokemon.heldItem) {
            this.sprites.heldItemIcon.visible = true;
            this.sprites.heldItemIcon.draw();
        } else {
            this.sprites.heldItemIcon.visible = false;
        }
    }
}
